import Analytics, { Events } from 'analytics/Analytics'
import dateToTimeString from 'components/calendar/components'
import { parseErrorToMessage } from 'components/popups/eventInfo/EventPopup'
import { setAlertTimeToReminders } from 'components/providers/CalendarSyncProdivder'
import { processDownloadedData } from 'components/providers/GetChangesProvider'
import {
  generalStatusToOutlook,
  getAuthProvider,
  outlookEventToTfmEvent,
  tfmEventToOutlook
} from 'components/providers/outlook/OutlookProvider'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { getDefaultTimezone } from 'index'
import Api from 'services/Api'
import ChangesService from 'services/db/Changes'
import Database from 'services/db/Database'
import { updateInheritanceNotices } from 'services/db/Queries/AppConstantQueries'
import {
  excludeInstance,
  localEventById,
  localEventByLocalId
} from 'services/db/Queries/Event'
import {
  localGroupById,
  setGroupSortOrderByEvent
} from 'services/db/Queries/GroupQueries'
import eventBus, { AppEvents } from 'services/EventBus'
import IcloudApi from 'services/IcloudApi'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  ChangeDoc,
  ChangeDocsByTypes,
  DocType,
  EventDoc,
  EventParticipantStatus,
  EventStatus,
  GroupDoc,
  TaskType
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import {
  parseStorageQuotaServerError,
  type StorageQuotaServerError
} from 'utils/api/errors'
import { isSomeDay } from 'utils/api/events'
import {
  deleteGoogleEvent,
  patchGoogleEvent,
  updateGoogleEvent
} from 'utils/api/google_events'
import {
  deleteEvent,
  getEventDetailsById,
  updateAttendeeStatus,
  updateEvent
} from 'utils/api/GraphService'
import { isConvertedGroup } from 'utils/api/group_state'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { is24meApp, isGroupcalApp } from 'utils/appType'
import {
  CurrentConvertedCalendars,
  googleEventToGroupcal,
  groupcalEventToGoogleEvent
} from 'utils/google_events'
import { groupToProvider, ProviderType } from 'utils/groups'
import { isTrulyEmpty, prepareDocForSaving } from 'utils/object'
import { isGuest } from 'utils/user'

import { parseDataWithCast } from './changes'
import { genericUpdateFields } from './group'

export const API_GET_EVENTS = '/tasks/'
export const API_EVENT_URL = '/tasks/edit/'
export const API_ADD_EVENT = '/tasks/add'
export const API_MOVE_EVENT = '/v1/task/move'
export const PARTICIPANT_STATUS = '/v1/eventsParticipantsStatus/batch'
export const API_BATCH_EVNTS = '/v1/task/edit/batch'

export interface EventBatchData {
  ok: boolean
  id: string
  rev: string
  doc?: EventDoc
}

export interface MoveEventResponse {
  deletedTask?: EventDoc
  newTask?: EventDoc
}

function emptyChangeDocsByTypes(): ChangeDocsByTypes {
  return {
    Group: {},
    GroupEvent: {},
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }
}

function appendChangeDoc(docs: ChangeDocsByTypes, doc?: ChangeDoc) {
  if (!doc?._id) return

  const docType = (doc.Type as DocType | undefined) ?? DocType.EVENT
  docs[docType][doc._id] = doc
}

async function buildEventDataForServer(
  event: Model<EventDoc> | EventDoc,
  data: EventDoc,
  ignoreLate?: boolean
) {
  const startOfItemForServer =
    data.AllDay === '1'
      ? dayjs
          .utc(
            dayjs(Number(data.StartDate ?? event.StartDate) * 1000).format(
              'YYYY-MM-DD'
            )
          )
          .startOf('day')
          .unix()
          .toString()
      : event.lateOriginalStartTime && !ignoreLate
        ? event.lateOriginalStartTime
        : (data.StartDate ?? event.StartDate)

  const endOfItemForServer =
    data.AllDay === '1'
      ? dayjs
          .utc(
            dayjs(Number(data.EndDate ?? event.EndDate) * 1000).format(
              'YYYY-MM-DD'
            )
          )
          .add(1, 'day')
          .add(-1, 'second')
          .startOf('day')
          .unix()
          .toString()
      : event.lateOriginalEndTime && !ignoreLate
        ? event.lateOriginalEndTime
        : (data.EndDate ?? event.EndDate)

  const eventInLocalDB = await localEventById(event._id ?? '')
  const localDBData = Database.toServerObject(eventInLocalDB)

  let eventData: EventDoc = {
    ...localDBData,
    ...data,
    GroupID:
      is24meApp() && event.TaskType !== TaskType.personalEvent
        ? undefined
        : (data.GroupID ?? event.GroupID),
    Status:
      data.Status && data.Status.length > 0 ? data.Status : EventStatus.ACTIVE,
    TaskType:
      data.TaskType && data.TaskType.length > 0 ? data.TaskType : TaskType.meet,
    Priority:
      (data.Priority && data.Priority?.length != 0) ||
      (event.Priority && event.Priority?.length != 0)
        ? (data.Priority ?? event.Priority ?? '1')
        : '1',
    isDeleted: '0',
    Shared: 'null',
    Reminder:
      isGroupcalApp() && event.ThirdPartyID?.length === 0
        ? undefined
        : data.Reminder,
    Location:
      data.Location && Object.keys(data.Location).length > 0
        ? data.Location
        : undefined,
    TimeZoneNameID: data.AllDay === '1' ? 'UTC' : data.TimeZoneNameID,
    local_id:
      localDBData.local_id?.length === 0 ? undefined : localDBData.local_id,
    StartDate:
      isSomeDay(data) || localDBData.someday === '1'
        ? 'null'
        : startOfItemForServer,
    EndDate:
      isSomeDay(data) || localDBData.someday === '1'
        ? 'null'
        : endOfItemForServer
  }

  if (isGroupcalApp()) delete data['Label']

  delete data['inheritedGroups']

  eventData = {
    ...eventData,
    Reminder: setAlertTimeToReminders(
      Number(eventData.StartDate),
      eventData.Reminder
    )
  }

  if (typeof eventData.Notes === 'string' && eventData.Notes === 'null') {
    eventData.Notes = []
  }

  eventData = {
    ...eventData,
    Notes: eventData.Notes?.filter((note) => note.Note && note.Note.length > 0)
  }

  if (eventData.Recurrence && Object.keys(eventData.Recurrence).length === 0) {
    delete eventData['Recurrence']
  }

  if (eventData.TaskType !== TaskType.personalEvent) {
    delete eventData['ThirdPartyID']
  }

  return eventData
}

export async function saveMovedEventChanges(response: MoveEventResponse) {
  const parsedResponse = parseDataWithCast(response) as MoveEventResponse
  const docs = emptyChangeDocsByTypes()

  appendChangeDoc(docs, parsedResponse.deletedTask)
  appendChangeDoc(docs, parsedResponse.newTask)

  return processDownloadedData(docs)
}

export async function moveEventOnServer(
  event: Model<EventDoc> | EventDoc,
  data: EventDoc,
  ignoreLate?: boolean
) {
  const eventData = await buildEventDataForServer(event, data, ignoreLate)

  const response = await Api.post(API_MOVE_EVENT, {
    OldTaskID: event._id,
    ...genericUpdateFields(),
    NewTask: {
      ...eventData,
      late: 0,
      attendeesChanged: 0
    }
  })

  if (!response.data) return

  return saveMovedEventChanges(response.data as MoveEventResponse)
}

function buildEventItemErrorPayload(
  error: any,
  eventId?: string,
  isCurrentUserGroupOwner: boolean = false
) {
  const storageQuotaError: StorageQuotaServerError | null =
    parseStorageQuotaServerError(error)

  if (storageQuotaError?.byPhotoQuota) {
    return {
      error,
      message: isCurrentUserGroupOwner
        ? t('photoLimitReachedBody', {
            photoQuotaLimit: storageQuotaError.photoQuotaLimit ?? '?',
            nextTierName: t('aHigherTier')
          })
        : t('photoLimitReachedBodyNotOwner'),
      title: t('photoLimitReachedTitle'),
      storageQuotaError
    }
  }

  return {
    error,
    message: parseErrorToMessage(error, eventId),
    title: t('cantDoThat'),
    storageQuotaError: null
  }
}

export async function uploadEventsBatch(tasks: EventDoc[]) {
  Logger.pink('uploadEventsBatch:Tasks', tasks)

  const response = await Api.post(API_BATCH_EVNTS, {
    ...genericUpdateFields(),
    tasks: tasks
  })

  Logger.pink('uploadEventsBatch:Response', response.data)

  return response.data as EventBatchData[]
}

export async function getEventFromServer(id: string) {
  const response: EventDoc = (await Api.get(`${API_GET_EVENTS}${id}`, {})).data
  Logger.blue('Event:FromServer', response)
  return response
}

export async function fetchAndSaveEventFromServer(id: string) {
  return getEventFromServer(id).then((event) => {
    return updateLocally({ ...event }, true)
  })
}

export async function updateEventOnServer(
  event: Model<EventDoc> | EventDoc,
  data: EventDoc,
  ignoreLate?: boolean
) {
  Logger.pink('UpdateEvent:Data', data)
  Logger.pink('UpdateEvent:Event', event)

  LocalStorage.set(
    LocalStorageKey.FOCUS_EVENT,
    `${event._id}${event.StartDate}`
  )

  let eventData = await buildEventDataForServer(event, data, ignoreLate)

  Logger.pink('EventUpdate:sending to server', eventData)
  const group = await localGroupById(eventData.GroupID ?? '')
  if (isConvertedGroup(group)) {
    // process google event
    const provider = groupToProvider(group)
    Logger.pink('Provider for event', provider)
    Logger.pink('ThirdPartyID for event', eventData.ThirdPartyID)

    if (provider === ProviderType.GOOGLE) {
      if (
        data.Status === EventStatus.DELETED ||
        event.Status === EventStatus.REMOVED
      ) {
        if (event.ThirdPartyID) {
          await deleteGoogleEvent(
            event.ThirdPartyID,
            event.local_id ?? ''
          ).then((result) => {
            if (result.status === 204) {
              return updateLocally({
                ...eventData,
                LastUpdate: dayjs().unix().toString(),
                Status: EventStatus.DELETED,
                needSync: '1'
              })
            }
          })
        }
      } else {
        await (
          data.attendeesChanged === true
            ? patchGoogleEvent(
                event.ThirdPartyID && event.ThirdPartyID?.length > 0
                  ? (event.ThirdPartyID ?? '')
                  : 'primary',
                groupcalEventToGoogleEvent(eventData, true)
              )
            : updateGoogleEvent(
                event.ThirdPartyID && event.ThirdPartyID?.length > 0
                  ? (event.ThirdPartyID ?? '')
                  : 'primary',
                groupcalEventToGoogleEvent(eventData)
              )
        ).then((result) => {
          if (result.status === 200) {
            return updateLocally({
              ...googleEventToGroupcal(result.data),
              OwnerID: eventData.OwnerID,
              PersonalOwnerId: eventData.PersonalOwnerId,
              GroupID: eventData.GroupID,
              ThirdPartyID: eventData.ThirdPartyID,
              needSync: '1',
              Color: eventData.Color,
              _rev: event._rev,
              _id: event._id
            })
          }
        })
      }
    } else if (provider === ProviderType.OUTLOOK) {
      const outlookEmail = CurrentConvertedCalendars.find(
        (ical) => ical.id === eventData.ThirdPartyID
      )?.primaryCalendarEmail
      const outlookAuthProvider = getAuthProvider(outlookEmail)

      if (outlookAuthProvider === undefined || outlookEmail === undefined) {
        console.error(
          'Outlook authprovider ',
          outlookAuthProvider,
          ' email ',
          outlookEmail
        )
        throw Error('No outlook auth provider found')
      } else {
        Logger.pink(
          'Outlook auth provider found for event update',
          outlookAuthProvider
        )
      }

      if (
        data.Status === EventStatus.DELETED ||
        event.Status === EventStatus.REMOVED
      ) {
        if (event.local_id) {
          await deleteEvent(
            outlookAuthProvider!,
            data.local_id ?? event.local_id ?? ''
          )
            .then((result) => {
              if (data.local_id?.includes('OID')) {
                // we updating instance of recurring event, so we need to exclude it
                return excludeInstance(
                  Number(eventData.StartDate) * 1000,
                  eventInLocalDB,
                  EventStatus.DELETED
                ).then(() => {
                  return
                })
              }

              return updateLocally({
                ...eventData,
                LastUpdate: dayjs().unix().toString(),
                Status: EventStatus.DELETED,
                needSync: '1',
                OwnerID: eventData.OwnerID,
                PersonalOwnerId: eventData.PersonalOwnerId,
                GroupID: eventData.GroupID,
                ThirdPartyID: eventData.ThirdPartyID,
                Color: eventData.Color,
                _rev: event._rev,
                _id: event._id
              })
            })
            .catch((error) => {
              // Database.write(async () => {
              //   event.update(
              //     (local: { Status: EventStatus; needSync: string }) => {
              //       local.Status = EventStatus.DELETED
              //       local.needSync = '1'
              //     }
              //   )
              // })
            })
        } else {
          throw Error('No local_id for outlook event to delete')
        }
      } else {
        Logger.blue('Attendees', data.attendeesChanged)
        Logger.blue(
          'Attendees',
          data.attendees?.some(
            (attendee) => attendee.responseStatus === 'needsAction'
          )
        )

        if (data.attendeesChanged && event.PersonalOwnerId !== outlookEmail) {
          // we need to update status on remote server
          const newStatus =
            generalStatusToOutlook(
              data.attendees?.find((attendee) => attendee.self)?.responseStatus
            ) ?? ''
          Logger.pink('newStatus', newStatus)
          if (newStatus && event.PersonalOwnerId !== outlookEmail) {
            const updatedStatusEvent = await updateAttendeeStatus(
              outlookAuthProvider!,
              newStatus,
              event.local_id ?? ''
            )
              .then(() => {
                return getEventDetailsById(
                  outlookAuthProvider!,
                  event.local_id ?? ''
                )
              })
              .then((updatedEvent) => {
                if (!updatedEvent) return Promise.resolve()
                Logger.orange(
                  'UpdateEvent:Updating attendee status completed',
                  updatedEvent
                )
                return updateLocally({
                  ...outlookEventToTfmEvent(updatedEvent, outlookEmail!),
                  needSync: '1',
                  OwnerID: eventData.OwnerID,
                  PersonalOwnerId: data.PersonalOwnerId,
                  GroupID: data.GroupID ?? event.GroupID,
                  ThirdPartyID: eventData.ThirdPartyID,
                  Color: eventData.Color,
                  _rev: event._rev,
                  _id: event._id
                })
              })
          }
          return event
        }
        const outlookEvent = await tfmEventToOutlook({
          ...eventData,
          _id: data.local_id ?? event.local_id ?? ''
        })

        let updatedOutlookEvent = await updateEvent(
          outlookAuthProvider!,
          data.local_id ?? event.local_id ?? '',
          outlookEvent
        )

        if (updatedOutlookEvent.recurrence) {
          const updatedEvent = await getEventDetailsById(
            outlookAuthProvider!,
            data.local_id ?? event.local_id ?? '',
            true
          )
          if (updatedEvent) {
            updatedOutlookEvent = updatedEvent
          } else {
            console.error('Outlook event not found')
            return
          }
        }

        if (data.local_id?.includes('OID')) {
          // we updating instance of recurring event, so we need to exclude it
          await excludeInstance(
            Number(eventData.StartDate) * 1000,
            eventInLocalDB,
            EventStatus.DELETED
          )

          const eventLocalId = await localEventByLocalId(event.local_id ?? '')

          // but then locally we need to save the instance event
          await updateLocally({
            ...outlookEventToTfmEvent(updatedOutlookEvent, outlookEmail!),
            OwnerID: eventData.OwnerID,
            PersonalOwnerId: data.PersonalOwnerId,
            GroupID: data.GroupID,
            ThirdPartyID: eventData.ThirdPartyID,
            needSync: '1',
            Color: eventData.Color,
            _rev: eventLocalId?._rev ?? '',
            _id: eventLocalId?._id ?? '',
            _local_id: event.local_id
          })
          Logger.blue(
            'UpdateEvent:Syncing local calendars to server after excluding instance'
          )
          syncLocalCalendarsToServer()
          return
        }

        await updateLocally({
          ...outlookEventToTfmEvent(updatedOutlookEvent, outlookEmail!),
          OwnerID: eventData.OwnerID,
          PersonalOwnerId: data.PersonalOwnerId,
          GroupID: data.GroupID,
          ThirdPartyID: eventData.ThirdPartyID,
          needSync: '1',
          Color: eventData.Color,
          _rev: event._rev,
          _id: event._id
        })
      }
    } else if (provider === ProviderType.APPLE) {
      const calendarAttendees = eventData.attendees?.map((attendee) => {
        return {
          Email: attendee.email,
          ResponseStatus:
            attendee?.responseStatus?.toUpperCase() &&
            attendee?.responseStatus?.toLowerCase()?.includes('needs')
              ? 'NEEDS-ACTION'
              : attendee?.responseStatus?.toUpperCase()
        }
      })
      eventData = {
        ...eventData,
        Attendees: calendarAttendees,
        TimeZoneNameID: isTrulyEmpty(eventData.TimeZoneNameID)
          ? getDefaultTimezone()
          : eventData.TimeZoneNameID
      }
      Logger.pink('ICLOUD:CurrentConvertedCalendars', CurrentConvertedCalendars)
      Logger.pink('ICLOUD:EventData', eventData)
      Logger.pink('ICLOUD:Data', data.ThirdPartyID)
      const appleCalendar = CurrentConvertedCalendars.find(
        (iCalendar) =>
          iCalendar.id ===
          (isTrulyEmpty(data.ThirdPartyID)
            ? eventData.ThirdPartyID
            : data.ThirdPartyID)
      )
      const email = appleCalendar?.primaryCalendarEmail
      Logger.pink('ICLOUD:Apple calendar email', email)

      if (
        data.Status === EventStatus.DELETED ||
        event.Status === EventStatus.REMOVED
      ) {
        // delete event
        await IcloudApi.deleteCalendarItemFromIcloud({
          email: email ?? '',
          url: eventData.Url
        }).then((result) => {
          Logger.blue('ICLOUD:DeletedEvent ', result)
          if (result.status === 200) {
            return updateLocally({
              ...eventData,
              needSync: '1',
              Color: eventData.Color,
              UserID: eventData.UserID,
              OwnerID: eventData.OwnerID,
              PersonalOwnerId: eventData.PersonalOwnerId,
              ThirdPartyID: eventData.ThirdPartyID,
              GroupID: eventData.GroupID,
              LastUpdate: dayjs().unix().toString(),
              Status: EventStatus.DELETED,
              _rev: eventData._rev,
              _id: eventData._id,
              local_id: eventData.local_id
            })
          }
        })
      } else {
        // update event

        if (data.Location && Object.values(data.Location).length === 0) {
          delete data.Location
        }

        await IcloudApi.updateCalendarItemToIcloud({
          email: email ?? '',
          url: eventData.url || eventData.Url,
          etag: event.etag,
          event: {
            ...eventData,
            _id: event.local_id,

            Location:
              eventData.Location && typeof eventData.Location !== 'string'
                ? {
                    ...eventData.Location,
                    Lat: String(eventData?.Location?.Lat),
                    Long: String(eventData?.Location?.Long)
                  }
                : undefined
          } // we enforce local_id as a param to update an event because we first update
        })
          .then((data) => {
            const eventDoc: EventDoc = data[0]
            Logger.blue('ICLOUD:UpdatedEvent ', eventDoc)
            return updateLocally({
              ...eventDoc,
              needSync: '1',
              OwnerID: eventData.OwnerID,
              Color: eventData.Color,
              UserID: eventData.UserID,
              TaskType: eventData.TaskType,
              PersonalOwnerId: eventData.PersonalOwnerId,
              ThirdPartyID: eventData.ThirdPartyID,
              GroupID: eventData.GroupID,
              LastUpdate: eventData.LastUpdate,
              _rev: eventData._rev,
              _id: eventData._id,
              local_id: eventData.local_id
            })
          })
          .catch((error) => {
            const eventDoc: EventDoc = error.response.data._original.event
            delete eventDoc.ThirdPartyLastUpdate
            delete eventDoc.LastUpdate
            Logger.pink('ICLOUD:Error', eventDoc)

            updateLocally(eventDoc).then(() => {
              eventBus.publish(AppEvents.EventItemError, {
                name: AppEvents.EventItemError,
                data: {
                  error: error,
                  message: parseErrorToMessage(error, event._id),
                  title: t('cantDoThat')
                }
              })
            })

            throw Error('Error updating event')
          })
      }
    }
    Logger.blue('UpdateEvent:Syncing local calendars to server after update')
    syncLocalCalendarsToServer()
  } else {
    if (eventData.Type === DocType.TASK) {
      delete eventData['GroupID']
    }

    const response = await Api.post(`${API_EVENT_URL}${event._id}`, {
      ...genericUpdateFields(),
      ...eventData,
      late: 0,
      attendeesChanged: 0
    })

    if (!response.data) return

    let eventFromServer: EventDoc = response.data['doc']

    if (!eventFromServer) return

    if (isGroupcalApp()) {
      eventFromServer = {
        ...eventFromServer,
        Reminder: data.Reminder
      }
    }

    await updateLocally(eventFromServer)

    return Database.write(async () => {
      ;(await localEventById(event._id ?? '')).update((local) => {
        const eventStart = dayjs(Number(local.StartDate) * 1000)
        local.LastUpdateMessage = {
          title: local.Text,
          type:
            event.Status === EventStatus.DELETED ||
            event.Status === EventStatus.REMOVED
              ? `${t('eventDeleted')}`
              : `${t('eventUpdated')}`,
          who: t('you'),
          when: `${eventStart.format('MMM DD YYYY')}, ${dateToTimeString(
            eventStart.toDate(),
            true
          )}`
        }
        local.LastUpdate = eventFromServer.LastUpdate
      })
    })
  }
}

async function updateLocally(
  eventFromServer: EventDoc,
  ignoreEventsForMessage?: boolean
) {
  Logger.blue('UpdateEvent:Updating event locally', eventFromServer)
  const statusOfEvent = eventFromServer['Status']
  const priority = eventFromServer['Priority']
  const eventChangeDoc: Record<string, EventDoc> = prepareDocForSaving({
    ...eventFromServer,
    Status: String(statusOfEvent),
    Priority: String(priority),
    Location: eventFromServer['Location']
  })
  const docs: ChangeDocsByTypes = {
    Group: {},
    GroupEvent: eventChangeDoc,
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }

  if (isGroupcalApp()) {
    Logger.blue('Sort order', Number(eventFromServer.LastUpdate))
    // LastUpdate might come with millis like:  1759933332.06, we need to drop that
    Logger.blue('UpdateEvent:Local update', eventFromServer)
    await setGroupSortOrderByEvent(
      eventFromServer,
      Math.floor(Number(eventFromServer.LastUpdate))
    )
  }

  if (
    String(statusOfEvent) === EventStatus.DELETED ||
    String(statusOfEvent) === EventStatus.REMOVED
  ) {
    Analytics.getInstance().sendEvent(Events.ITEM_DELETED, {})
  }

  return await processDownloadedData(docs, ignoreEventsForMessage)
}

export interface UpdateParticipantStatusBody {
  //event id
  _id?: string
  ParticipantsStatus?: EventParticipantStatus
  DeviceChangeID?: string
}

export async function updateParticipantStatus(
  statuses: UpdateParticipantStatusBody[]
) {
  const response = await Api.post(`${PARTICIPANT_STATUS}`, {
    Events: statuses
  })

  const updatedStatuses: Record<string, EventDoc> = parseDataWithCast(
    response.data
  )

  const eventsWithStatuses: ChangeDocsByTypes = {
    Group: {},
    GroupEvent: updatedStatuses,
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }
  ChangesService.saveChanges(eventsWithStatuses)

  return updatedStatuses
}

export interface GuestGroupData {
  group?: GroupDoc
  groups: GroupDoc[]
  parentGroups?: GroupDoc[]
  events: EventDoc[]
  recurrings: EventDoc[]
  groupsExceedsInheritanceDepthLimit?: Record<string, 1>
  groupsInheritanceBlockedByTier?: Record<string, 1>
  canRemovePoweredBy: boolean
  canUseCardsView: boolean
}

export async function getEventsForGroup(
  groupId: string,
  fromSeconds: number,
  toSeconds: number,
  groupIds?: string[],
  ignoreProcessing?: boolean
) {
  let url = `/v1/groups${
    (groupIds ?? []).length > 0 ? '/' : `/${groupId}/`
  }events?fromDate=${fromSeconds}&toDate=${toSeconds}`

  if (groupIds && groupIds.length > 0) {
    for (const group of groupIds) {
      url += `&groupIds[]=${group}`
    }
  }

  const events = await Api.get(url)

  const guestData = events.data as GuestGroupData

  const groupEvents: Record<string, EventDoc> = {}
  const groups: Record<string, GroupDoc> = {}

  guestData.events.forEach((event) => {
    groupEvents[event._id ?? ''] = event
  })

  guestData.recurrings.forEach((event) => {
    groupEvents[event._id ?? ''] = event
  })

  if (guestData.group) {
    guestData.groups = [guestData.group, ...(guestData.parentGroups ?? [])]
  }

  guestData.groups?.forEach((group) => {
    groups[group._id ?? ''] = group
  })

  await updateInheritanceNotices(
    {
      groupsExceedsInheritanceDepthLimit:
        guestData.groupsExceedsInheritanceDepthLimit,
      groupsInheritanceBlockedByTier: guestData.groupsInheritanceBlockedByTier
    },
    groupIds?.length ? groupIds : [groupId]
  )

  const eventsWithStatuses: ChangeDocsByTypes = {
    Group: groups,
    GroupEvent: groupEvents,
    Account: {},
    MasterLabel: {},
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }
  if (!ignoreProcessing) {
    if (isGuest()) {
      Database.clearCollection(CollectionType.EVENT).then(() => {
        return ChangesService.saveChanges(eventsWithStatuses)
      })
    } else {
      ChangesService.saveChanges(eventsWithStatuses)
    }
  }

  Logger.debug(
    'API: getEventsForGroup',
    Object.keys(groupEvents).length,
    groupEvents
  )
  return events.data as GuestGroupData
  // before save, we need to remove all events to keep only the latest
}
